import { getRequest, getRequestHistory, getHistoryPreviousMessages, getHistoryStatus, updateRequestHistory } from 'Api/quoteRequestApi'
import { parseChatCompany, parseChatCompanyStatus, parseChatMessage, parseTemporaryMessage, parseSystemMessage } from 'Parsers/chatParser'
import { formatChatMessage } from 'Formatters/chatFormatter'
import unionBy from 'lodash/unionBy'
import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'
import cloneDeep from 'lodash/cloneDeep'
import messageStatus from 'Enums/messageStatus'
import historyOrigin from 'Enums/historyOrigin'
import { chatSignalR } from 'Services/signalR'
import { default as compareAscDate } from 'date-fns__legacy__/compare_asc'
import { default as parseDate } from 'date-fns__legacy__/parse'
import chatListStatus from 'Enums/chatListStatus'

const state = {
  quotationId: null,
  connectionId: null,

  requesting: {
    chatRealtime: false,
    companyList: false,
    companyStatus: false,
    lastMessages: false,
    newChat: false,
    previousMessages: false,
    sendMesssage: false,
  },

  selectedChat: {
    companyId: null,
    negotiationId: null,
  },

  companyList: [],
  chatHistory: [],
  uploadList: [],
  unsentMessages: [],
  mobileDetailsView: {
    chat: true,
    requestInfo: false,
    tips: false,
  },
  currentPage: 1,
  totalCount: 0,
}

const getters = {
  company: state => {
    const selectedCompany = company => company.id === state.selectedChat.companyId

    return state.companyList.find(selectedCompany) || null
  },
}

const mutations = {

  request_start(state, type) {
    state.requesting[type] = true
  },

  request_success(state, type) {
    state.requesting[type] = false
  },

  request_failure(state, type) {
    state.requesting[type] = false
  },

  set_quotation_id(state, quotationId) {
    state.quotationId = quotationId
  },

  set_company_list(state, companyList) {
    state.companyList = companyList
  },

  set_chat_history(state, chatHistory) {
    state.chatHistory = chatHistory
  },

  reset_chat_history(state) {
    state.chatHistory.length = 0
  },

  set_upload_list(state, uploadList) {
    state.uploadList = uploadList
  },

  set_unsent_messages(state, unsentMessages) {
    state.unsentMessages = unsentMessages
  },

  set_selected_chat(state, selectedChat) {
    Object.assign(state.selectedChat, selectedChat)
  },

  set_connection_id(state, connectionId) {
    state.connectionId = connectionId
  },

  set_mobile_view_chat(state) {
    state.mobileDetailsView.chat = true
    state.mobileDetailsView.requestInfo = false
    state.mobileDetailsView.tips = false
  },

  set_mobile_view_request_info(state) {
    state.mobileDetailsView.requestInfo = true
    state.mobileDetailsView.chat = false
    state.mobileDetailsView.tips = false
  },
  set_mobile_view_tips(state) {
    state.mobileDetailsView.tips = true
    state.mobileDetailsView.chat = false
    state.mobileDetailsView.requestInfo = false
  },
  set_current_page(state, currentPage) {
    state.currentPage = currentPage
  },
  set_total_count(state, totalCount) {
    state.totalCount = totalCount
  }
}

const actions = {

  openRequestInfoMobile({ commit }) {
    commit('set_mobile_view_request_info')
  },

  openChatMobile({ commit }) {
    commit('set_mobile_view_chat')
  },

  openTipsMobile({ commit }) {
    commit('set_mobile_view_tips')
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @returns {Promise.<import('@aspnet/signalr').HubConnection>}
   */
  async openChatConnection({ commit }) {
    commit('request_start', 'chatRealtime')

    try {
      const connection = await chatSignalR()

      const connectionId = await connection.invoke('getConnectionId')
      commit('set_connection_id', connectionId)

      commit('request_success', 'chatRealtime')

      return connection
    } catch (err) {
      commit('request_failure', 'chatRealtime')

      throw err
    }
  },

  /**
  * @param {import('vuex').ActionContext} context
  * @param {import('@aspnet/signalr').HubConnection} connection
  */
  async closeChatConnection({ commit }, connection) {
    commit('request_start', 'chatRealtime')

    try {
      const result = await connection.stop()
      commit('set_connection_id', null)

      commit('request_success', 'chatRealtime')

      return result
    } catch (err) {
      commit('request_failure', 'chatRealtime')

      throw err
    }
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @param {number} quotationId
   */
  async getCompanyList({ commit, dispatch }, quotationId) {
    commit('request_start', 'companyList')

    try {
      const { data } = await getRequest(quotationId)

      const companyList = data.interestedCompanies.map(parseChatCompany)
      commit('set_company_list', companyList)

      commit('set_quotation_id', quotationId)
      dispatch('getCompanyStatus')

      commit('request_success', 'companyList')

      return true
    } catch (err) {
      commit('request_failure', 'companyList')

      throw err
    }
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @param {ChatCompany} company
   */
  async getCompanyStatus({ commit, state }) {
    commit('request_start', 'companyStatus')

    try {
      const { data } = await getHistoryStatus(state.quotationId)

      const companyList = state.companyList.map(company => {
        const status = data.find(item => item.companyId === company.id)

        return status
          ? parseChatCompanyStatus(company, status)
          : company
      })

      commit('set_company_list', companyList)

      commit('request_success', 'companyStatus')

      return data
    } catch (err) {
      commit('request_failure', 'companyStatus')

      throw err
    }
  },

  /**
   * @param {import('vuex').ActionContext} context
   */
  clearChatList({ commit }) {
    commit('set_company_list', [])
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @param {ChatHistoryMessage[]} newMessages
   */
  updateChatHistory({ commit, state }, newMessages = []) {
    const { chatHistory } = state

    const isSendingMessages = chatHistory.some(msg => msg.status === messageStatus.sending)

    const mergedMessages = isSendingMessages
      ? unionBy(chatHistory, newMessages, 'id')
      : unionBy(newMessages, chatHistory, 'id')

    const uniqueMessages = uniqBy(mergedMessages, 'id')
    const newHistory = orderBy(uniqueMessages, 'id')

    commit('set_chat_history', newHistory)

    return newHistory
  },

  /**
   * @param {import('vuex').ActionContext} context
   */
  addWelcomeMessage({ state, dispatch, getters }) {
    const { chatHistory } = state
    const newMessageList = cloneDeep(chatHistory)

    const systemMessage = parseSystemMessage(`
      ${getters.company.name} está participando da sua solicitação.

      Faça perguntas sempre que precisar 😉
    `.trim())

    newMessageList.push(systemMessage)
    dispatch('updateChatHistory', newMessageList)

    return systemMessage
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @param {number} companyId
   */
  async getLastMessages({ commit, state, dispatch }, companyId) {
    commit('request_start', 'lastMessages')

    try {
      const { quotationId, connectionId } = state
      const { data } = await getRequestHistory(quotationId, companyId, connectionId)
      commit('set_total_count', data.totalCount)

      const { items: messageList = [] } = data
      const newMessages = messageList.map(parseChatMessage)

      dispatch('updateChatHistory', newMessages)

      if (newMessages.length === 0) {
        dispatch('addWelcomeMessage')
      }

      commit('request_success', 'lastMessages')

      return data
    } catch (err) {
      commit('request_failure', 'lastMessages')

      throw err
    }
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @returns {Promise.<ChatHistoryMessage[]>}
   */
  async getPreviousMessages({ commit, state, dispatch }) {
    commit('request_start', 'previousMessages')

    try {
      const { negotiationId } = state.selectedChat
      const { chatHistory } = state

      const todayDate = parseDate(Date.now())

      const dateList = chatHistory.map(item => item.datetime)
      const [firstDate = todayDate] = dateList.sort(compareAscDate)
      commit('set_current_page', state.currentPage + 1)

      const { data = [] } = await getHistoryPreviousMessages(negotiationId, state.currentPage)
      const newMessages = data.map(parseChatMessage)

      dispatch('updateChatHistory', newMessages)

      commit('request_success', 'previousMessages')

      return newMessages
    } catch (err) {
      commit('request_failure', 'previousMessages')

      throw err
    }
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @param {ChatMessage} chatMessage
   */
  async sendMessage({ commit, dispatch, state }, chatMessage) {
    const { negotiationId } = state.selectedChat

    const temporaryMessage = await dispatch('addTemporaryMessage', chatMessage)

    try {
      const { identifier } = temporaryMessage

      const apiChatMessage = formatChatMessage(chatMessage, negotiationId, identifier)
      const { data } = await updateRequestHistory(apiChatMessage)

      dispatch('updateMessage', data)

      return data
    } catch (err) {
      commit('set_unsent_messages', temporaryMessage.id)
      commit('request_failure', 'sendMesssage')

      throw err
    }
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @param {ChatMessage} chatMessage
   */
  addTemporaryMessage({ commit, state, rootState }, chatMessage) {
    const { user, company } = rootState.account

    const temporaryMessage = parseTemporaryMessage(
      chatMessage,
      user.name,
      company.name,
      historyOrigin.customer
    )

    const newHistory = [
      ...cloneDeep(state.chatHistory),
      temporaryMessage
    ]

    commit('set_chat_history', newHistory)

    return temporaryMessage
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @param {ApiChatHistoryMessage} apiMessage
   */
  addMessage({ commit, state }, apiMessage) {
    const newMessage = parseChatMessage(apiMessage)

    const newHistory = [
      ...cloneDeep(state.chatHistory),
      newMessage
    ]

    commit('set_chat_history', newHistory)

    return newMessage
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @param {ApiChatHistoryMessage} apiMessage
   */
  updateMessage({ commit, state }, apiMessage) {
    const previousHistory = cloneDeep(state.chatHistory)

    const newMessage = parseChatMessage(apiMessage)

    const newHistory = previousHistory.map(item =>
      (item.identifier === newMessage.identifier) ? newMessage : item
    )

    commit('set_chat_history', newHistory)

    return newMessage
  },

  /**
 * @param {import('vuex').ActionContext} context
 */
  markMessageRead({ commit, state }) {
    const newHistory = cloneDeep(state.chatHistory)

    const msgNotRead = newHistory.filter(item => item.status !== messageStatus.read)
    msgNotRead.forEach(item => item.status = messageStatus.read)

    commit('set_chat_history', newHistory)

    return msgNotRead
  },

  /**
   * @param {import('vuex').ActionContext} context
   * @param {number} companyId
   */
  async openChat({ commit, dispatch }, companyId) {
    commit('request_start', 'newChat')

    try {
      commit('reset_chat_history')
      commit('set_selected_chat', { companyId })

      const data = await dispatch('getLastMessages', companyId)

      const { companyQuotationId: negotiationId } = data
      commit('set_selected_chat', { negotiationId })

      commit('request_success', 'newChat')

      return negotiationId
    } catch (err) {
      commit('set_selected_chat', { negotiationId: null, companyId: null })
      commit('request_failure', 'newChat')

      throw err
    }
  },

  /**
   * @param {import('vuex').ActionContext} context
   */
  async closeChat({ commit }) {
    const selectedChat = {
      companyId: null,
      negotiationId: null,
    }

    commit('reset_chat_history')
    commit('set_selected_chat', selectedChat)
  },
}

export default {
  namespaced: true,

  state,
  getters,
  mutations,
  actions,
}